import { createSlice } from '@reduxjs/toolkit';
import type { ProjectContent, ProjectContentCategory } from '@services/dto/content';
import type { ProjectContentEx, ProjectFilesState } from './types';

const slice = createSlice({
  name: 'projectFiles',
  initialState: {
    loading: false,
    files: [],
    categoryFiles: [],
  } as ProjectFilesState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    setFiles: (state, action: { payload: { files: ProjectContent[]; accessToken: string } }) => {
      state.files = action.payload.files.map(
        (x: ProjectContent) =>
          ({
            ...x,
            discipline: x?.metadata?.discipline,
            disciplineById: x?.metadata?.by,
            classification: x?.metadata?.classification,
            score: x?.metadata?.score,
          }) as ProjectContentEx,
      );
      buildCategoryFiles(state, action.payload.accessToken);
      state.loading = false;
      state.error = undefined;
    },
    setCategory: (state, action) => {
      state.category = action.payload.category;
      buildCategoryFiles(state, action.payload.accessToken);
    },
    reset: (state) => {
      state.loading = false;
      state.files = [];
      state.category = undefined;
      state.categoryFiles = [];
      state.error = undefined;
    },
  },
});

export const projectFilesActions = slice.actions;
export const projectFilesReducer = slice.reducer;

const buildCategoryFiles = (state: ProjectFilesState, accessToken: string) => {
  state.categoryFiles = [];

  if (!state.files || !state.category) return;

  const content: ProjectContentEx[] = [...state.files];

  const prepCategory = (category: ProjectContentCategory) => {
    content
      .filter((c) => !c.parentId && c.category === category)
      .sort((a, b) => a.originalName.localeCompare(b.originalName))
      .forEach((cx) => {
        if (!state.categoryFiles) return;

        const c = { ...cx };
        state.categoryFiles.push(c);

        c.children = [];

        if (c.explodeInfo?.pages) {
          c.explodeInfo.pages.forEach((expInfo) => {
            const page = content.find((x) => x.parentId === c.id && x.originalName === expInfo.pageName);
            // Was the page found?
            if (!page) {
              c.children.push({ pageExplodeInfo: expInfo } as ProjectContentEx);
            }
          });
        }

        content
          .filter((c2) => c2.parentId === c.id)
          .forEach((c2x, idx) => {
            const c2 = { ...c2x };
            c.children.push(c2);

            if (c.explodeInfo?.pages) {
              c2.pageExplodeInfo = c.explodeInfo.pages.find((x) => x.pageName === c2.originalName);
              c2.pageIndex = idx;
            }

            c2.children = [];
            content
              .filter((c3) => c3.parentId === c2.id)
              .forEach((c3) => {
                if (c3.contentType.startsWith('image/')) {
                  if (!c2.images) c2.images = {};
                  if (c3.originalName.includes('-TH.'))
                    c2.images.thumb = c3.url.includes('sv=') ? c3.url : `${c3.url}?access_token=${accessToken}`;
                  else if (c3.originalName.includes('-MD.'))
                    c2.images.card = c3.url.includes('sv=') ? c3.url : `${c3.url}?access_token=${accessToken}`;
                  else c2.images.full = c3.url.includes('sv=') ? c3.url : `${c3.url}?access_token=${accessToken}`;
                }
                c2.children.push(c3);
              });
          });

        c.children = c.children.sort((a, b) => {
          const ax = a.pageExplodeInfo?.pageNo || 0;
          const bx = b.pageExplodeInfo?.pageNo || 0;
          if (ax < bx) return -1;
          return ax > bx ? 1 : 0;
        });
      });
  };

  if (Array.isArray(state.category)) {
    state.category.forEach((cat) => {
      prepCategory(cat);
    });
  } else prepCategory(state.category);
};
